import type { PromptVersion } from '@/types/prompt.types'

interface DiffViewProps {
  oldVersion: PromptVersion
  newVersion: PromptVersion
}

export function DiffView({ oldVersion, newVersion }: DiffViewProps) {
  const oldLines = oldVersion.prompt_content.split('\n')
  const newLines = newVersion.prompt_content.split('\n')
  const diff = computeSimpleDiff(oldLines, newLines)

  return (
    <div className="overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border-default)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-4 py-2">
        <span className="text-[var(--text-sm)] text-[var(--color-text-secondary)]">
          Comparing: v{oldVersion.version_number} &larr; v{newVersion.version_number}
        </span>
        <div className="flex items-center gap-3 text-[var(--text-xs)]">
          <span className="text-[var(--color-success)]">+{diff.additions}</span>
          <span className="text-[var(--color-error)]">-{diff.deletions}</span>
        </div>
      </div>

      {/* Diff lines */}
      <div className="overflow-x-auto bg-[var(--color-bg-primary)] font-mono text-[var(--text-xs)]">
        <table className="w-full border-collapse" role="presentation">
          <tbody>
            {diff.lines.map((line, i) => (
              <tr
                key={i}
                className={
                  line.type === 'added'
                    ? 'bg-[var(--color-success)]/10'
                    : line.type === 'removed'
                      ? 'bg-[var(--color-error)]/10'
                      : ''
                }
              >
                <td className="w-10 select-none border-r border-[var(--color-border-default)] px-2 py-0.5 text-right text-[var(--color-text-muted)]">
                  {line.oldLineNum ?? ''}
                </td>
                <td className="w-10 select-none border-r border-[var(--color-border-default)] px-2 py-0.5 text-right text-[var(--color-text-muted)]">
                  {line.newLineNum ?? ''}
                </td>
                <td className="w-6 select-none px-1 py-0.5 text-center">
                  {line.type === 'added' && (
                    <span className="text-[var(--color-success)]">+</span>
                  )}
                  {line.type === 'removed' && (
                    <span className="text-[var(--color-error)]">-</span>
                  )}
                </td>
                <td className="whitespace-pre-wrap px-2 py-0.5 text-[var(--color-text-primary)]">
                  {line.content}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged'
  content: string
  oldLineNum?: number
  newLineNum?: number
}

function computeSimpleDiff(
  oldLines: string[],
  newLines: string[],
): { lines: DiffLine[]; additions: number; deletions: number } {
  const lines: DiffLine[] = []
  let additions = 0
  let deletions = 0
  let oldIdx = 0
  let newIdx = 0

  // Simple line-by-line comparison (not a full Myers diff, but sufficient for display)
  const maxLen = Math.max(oldLines.length, newLines.length)

  for (let i = 0; i < maxLen; i++) {
    const oldLine = oldIdx < oldLines.length ? oldLines[oldIdx] : undefined
    const newLine = newIdx < newLines.length ? newLines[newIdx] : undefined

    if (oldLine === newLine) {
      lines.push({
        type: 'unchanged',
        content: oldLine ?? '',
        oldLineNum: oldIdx + 1,
        newLineNum: newIdx + 1,
      })
      oldIdx++
      newIdx++
    } else if (oldLine !== undefined && !newLines.includes(oldLine)) {
      lines.push({
        type: 'removed',
        content: oldLine,
        oldLineNum: oldIdx + 1,
      })
      deletions++
      oldIdx++
      i-- // Don't advance newIdx
    } else if (newLine !== undefined) {
      lines.push({
        type: 'added',
        content: newLine,
        newLineNum: newIdx + 1,
      })
      additions++
      newIdx++
      if (oldLine === newLine) oldIdx++
    }

    if (oldIdx > oldLines.length + newLines.length) break // safety valve
  }

  return { lines, additions, deletions }
}
